import React from "react";
import {DoubleFieldCellValue, StackedFieldCellRenderer} from "./DoubleFieldCellRenderer";
import {ManifestStop, Stop} from "../../../generated/graphql";
import {getStopCompletedTime, getStopPreposition} from "../../../utils/stops/StopTimeUtils";
import {
  formatDate,
  getTimeAtSpecificTimeZone,
  getLocalDate,
  getLocalTime,
  TimeFormatOptions
} from "../../../utils/DateTimeUtils";
import type {ICellRendererParams} from "@ag-grid-community/core";

import {toDate} from "date-fns-tz";
import {isCompleted, isOnSite} from "../../../utils/stops/StopUtils";
import {EmptyValueStrings} from "../../../constants/constants";
import {TManifestStopEnhanced} from "../types";

export enum StopDateTimeType {
  SCHEDULED_TIME,
  EARLY_TIME,
  LATE_TIME,
  POD_TIME,
  DEPARTURE_TIME,
  ARRIVAL_TIME,
  ETA,
  ETA_GENERATION_TIME,
  COMPLETED_TIME,
  ORDER_CREATED_DATE
}

export interface GetDateTimeCellValueProps {
  data: ManifestStop | Stop;
  is24HourFormat: boolean;
  isTimezoneOfJobStop: boolean;
  typeOfDateTime: StopDateTimeType;
  showPreposition?: boolean;
}

const getDoubleFieldCellValue = (localTime: string, localDate: string): DoubleFieldCellValue => {
  return {
    upperField: {
      value: localTime,
      style: {
        fontWeight: 400,
        lineHeight: "16px",
        color: "#111827"
      }
    },
    lowerField: {
      value: localDate,
      style: {
        fontWeight: "lighter",
        color: "#6B7280"
      }
    }
  };
};

export const getDate = (dateTimeString: string, timeZone: string, isTimezoneOfJobStop: boolean, format = "MMM dd") => {
  return isTimezoneOfJobStop
    ? formatDate(dateTimeString, timeZone, format)
    : getLocalDate(dateTimeString, timeZone, format);
};

export const getTime = (
  dateStr: string,
  timeZone: string,
  isTimezoneOfJobStop: boolean,
  isTimeFormat24hr: boolean,
  options: TimeFormatOptions = {includeZone: true}
) => {
  return isTimezoneOfJobStop
    ? getTimeAtSpecificTimeZone({dateStr, timeZone, isTimeFormat24hr, options})
    : getLocalTime({dateStr, timeZone, isTimeFormat24hr, options});
};

export const getTimeStampValueByType = (
  stop: TManifestStopEnhanced,
  timezoneOfJobStop: boolean,
  isTimeFormat24hr: boolean,
  type: StopDateTimeType = StopDateTimeType.SCHEDULED_TIME,
  options: {amPmFormat: string; includeZone: boolean} = {amPmFormat: "aaa", includeZone: false}
): string => {
  let time;

  switch (type) {
    case StopDateTimeType.SCHEDULED_TIME:
      if (isCompleted(stop)) {
        time = getStopCompletedTime(stop);
      } else {
        time = isOnSite(stop) ? stop.arriveDateTime : stop.scheduledDateTime;
      }
      break;
    case StopDateTimeType.ETA:
      time = stop.eta;
      break;
    default:
      time = "";
  }

  const timeStr = timezoneOfJobStop
    ? getTimeAtSpecificTimeZone({
        dateStr: time,
        timeZone: stop.timeZone,
        isTimeFormat24hr: isTimeFormat24hr,
        options: options
      })
    : getLocalTime({
        dateStr: time,
        timeZone: stop.timeZone,
        isTimeFormat24hr,
        options: options
      });

  if (timeStr) {
    return timeStr;
  }
  return EmptyValueStrings.notAvailable;
};

export const getDateTimeByType = (data: ManifestStop | Stop, type: StopDateTimeType): Date | undefined => {
  let datetime;
  const {timeZone} = data;
  switch (type) {
    case StopDateTimeType.SCHEDULED_TIME:
      datetime = data?.scheduledDateTime;
      break;
    case StopDateTimeType.EARLY_TIME:
      datetime = data?.earlyDateTime;
      break;
    case StopDateTimeType.LATE_TIME:
      datetime = data?.lateDateTime;
      break;
    case StopDateTimeType.POD_TIME:
      datetime = data?.podDateTime;
      break;
    case StopDateTimeType.DEPARTURE_TIME:
      datetime = data?.departDateTime;
      break;
    case StopDateTimeType.ARRIVAL_TIME:
      datetime = data?.arriveDateTime;
      break;
    case StopDateTimeType.ETA:
      if (!isCompleted(data as TManifestStopEnhanced)) datetime = (data as TManifestStopEnhanced)?.eta;
      break;
    case StopDateTimeType.ETA_GENERATION_TIME:
      if (!isCompleted(data as TManifestStopEnhanced)) datetime = (data as TManifestStopEnhanced)?.generatedDateTime;
      break;
    case StopDateTimeType.COMPLETED_TIME:
      datetime = getStopCompletedTime(data);
      break;
    case StopDateTimeType.ORDER_CREATED_DATE:
      datetime = data?.order?.createDate;
      break;

    default:
  }

  if (datetime && timeZone) {
    return toDate(datetime, {timeZone});
  }
  return undefined;
};

export const getStopDateTimeCellValue = ({
  isTimezoneOfJobStop,
  is24HourFormat,
  data,
  typeOfDateTime,
  showPreposition = true
}: GetDateTimeCellValueProps) => {
  if (!data) return undefined;

  const stopTime = getDateTimeByType(data, typeOfDateTime!);
  if (!stopTime || !data.timeZone) {
    return undefined;
  }

  let localTime = getTime(stopTime.toISOString(), data.timeZone, isTimezoneOfJobStop, is24HourFormat);

  const stopPrep = getStopPreposition(data);
  if (showPreposition && stopPrep) {
    localTime = `${stopPrep} ${localTime}`;
  }

  const localDate = getDate(stopTime.toISOString(), data.timeZone, isTimezoneOfJobStop);

  return getDoubleFieldCellValue(localTime, localDate!);
};

export const getStopDateCellValue = ({
  isTimezoneOfJobStop,
  data,
  typeOfDateTime
}: Omit<GetDateTimeCellValueProps, "showPreposition" | "is24HourFormat">) => {
  if (!data) return undefined;

  const stopTime = getDateTimeByType(data, typeOfDateTime!);
  if (!stopTime || !data.timeZone) {
    return undefined;
  }

  const localDate = getDate(stopTime.toISOString(), data.timeZone, isTimezoneOfJobStop, "MM/dd/yyyy zzz");

  return localDate;
};

export const DateTimeCellRenderer = (params: ICellRendererParams<DoubleFieldCellValue | undefined>) => {
  if (!params.value) {
    return <></>;
  }
  return StackedFieldCellRenderer(params.value);
};

export const DateCellRenderer = (params: ICellRendererParams<DoubleFieldCellValue | undefined>) => {
  if (!params.value) {
    return <></>;
  }
  return StackedFieldCellRenderer(params.value);
};
